/**
 * Thin wrapper around localStorage that:
 *   - Returns undefined (not null) on miss for cleaner type narrowing
 *   - Swallows SecurityError / QuotaExceeded (private browsing mode, etc.)
 *   - JSON-encodes/decodes the value automatically
 */

const TOKEN_KEY = 'st_token';
const USER_KEY = 'st_user';

function safeGet(key: string): string | undefined {
  try {
    return localStorage.getItem(key) ?? undefined;
  } catch {
    return undefined;
  }
}

function safeSet(key: string, value: string): void {
  try {
    localStorage.setItem(key, value);
  } catch {
    // private mode / quota — ignore silently
  }
}

function safeRemove(key: string): void {
  try {
    localStorage.removeItem(key);
  } catch {
    // ignore
  }
}

export function getToken(): string | undefined {
  return safeGet(TOKEN_KEY);
}

export function setToken(token: string): void {
  safeSet(TOKEN_KEY, token);
}

export function clearToken(): void {
  safeRemove(TOKEN_KEY);
}

export function getStoredUser<T>(): T | undefined {
  const raw = safeGet(USER_KEY);
  if (!raw) return undefined;
  try {
    return JSON.parse(raw) as T;
  } catch {
    safeRemove(USER_KEY);
    return undefined;
  }
}

export function setStoredUser<T>(user: T): void {
  safeSet(USER_KEY, JSON.stringify(user));
}

export function clearStoredUser(): void {
  safeRemove(USER_KEY);
}

export function clearAllAuth(): void {
  clearToken();
  clearStoredUser();
}
